import { useState, useEffect, useMemo, memo, FC } from "react";
import {
  Chart,
  CommonSeriesSettings,
  Series,
  ValueAxis,
  ArgumentAxis,
  Label,
  Export,
  Format,
  Grid,
  Tooltip,
  Legend,
  Size,
} from "devextreme-react/chart";
import { SelectBox } from "devextreme-react";
import { LoadPanel } from "devextreme-react/load-panel";
import { useFetch } from "src/hooks/useFetch";
import { SpatialRun } from "../types";
import { tileLayoutManager, WellCount } from "./TileLayoutManager";
import { tileRatesByName } from "./run-parameter-well-tiles";
import { useRunTileLabels } from "./use-run-tile-labels";

// ============ Types for API responses ============

// Common type for target registration stats
interface TargetProjectionRegistrationStats {
  Target: string;
  WellProjectionRegistrationSuccessRate: number;
  TileProjectionRegistrationSuccessRates: number[];
}

interface Failure {
  Code: string;
  Message: string;
}

// --- cellAssignment endpoint types (array of targets, object of run rates) ---
interface CellAssignmentWellStats {
  WellLocation: string;
  Failure?: Failure | null;
  TargetProjectionRegistrationStats?:
    | TargetProjectionRegistrationStats[]
    | null; // Array of targets
}

interface CellAssignmentBatch {
  BatchName: string;
  WellCellAssignmentStats?: CellAssignmentWellStats[];
  RunTargetProjectionRegistrationSuccessRates?: Record<string, number>; // Object keyed by target
}

type CellAssignmentResponse = Record<string, CellAssignmentBatch>;

// --- targetCellAssignment endpoint types (single target, single run rate) ---
interface TargetCellAssignmentWellStats {
  WellLocation: string;
  TargetProjectionRegistrationStats?: TargetProjectionRegistrationStats | null; // Single object
}

interface TargetCellAssignmentBatch {
  BatchName: string;
  WellCellTargetAssignmentStats?: TargetCellAssignmentWellStats[];
  RunTargetProjectionRegistrationSuccessRate?: number; // Single number
}

type TargetCellAssignmentResponse = Record<string, TargetCellAssignmentBatch>;

// --- rawCellAssignment endpoint types (single target "Avid", single run rate) ---
interface RawCellAssignmentWellStats {
  WellLocation: string;
  Failure?: Failure | null;
  TargetProjectionRegistrationStats?: TargetProjectionRegistrationStats | null; // Single object
}

interface RawCellAssignmentBatch {
  BatchName: string;
  WellCellRawAssignmentStats?: RawCellAssignmentWellStats[];
  RunTargetProjectionRegistrationSuccessRate?: number; // Single number
}

type RawCellAssignmentResponse = Record<string, RawCellAssignmentBatch>;

// --- cellPaint endpoint types (array of targets per well, object of run rates) ---
// WellCellPaintStats carries the same per-well/per-target projection-registration
// shape as the cellAssignment endpoint (an array of TargetProjectionRegistrationStats),
// so the tile-level heatmap can be driven from it.
interface CellPaintWellStats {
  WellLocation: string;
  Failure?: Failure | null;
  TargetProjectionRegistrationStats?:
    | TargetProjectionRegistrationStats[]
    | null; // Array of targets
}

interface CellPaintBatch {
  BatchName: string;
  WellCellPaintStats?: CellPaintWellStats[];
  RunTargetProjectionRegistrationSuccessRates?: Record<string, number>; // Object keyed by target
}

type CellPaintResponse = Record<string, CellPaintBatch>;

// --- Normalized types for internal use ---
interface NormalizedWellData {
  wellLocation: string;
  targets: TargetProjectionRegistrationStats[];
}

interface NormalizedBatch {
  batchName: string;
  source:
    | "cellAssignment"
    | "targetCellAssignment"
    | "rawCellAssignment"
    | "cellPaint";
  wells: NormalizedWellData[];
  runRates: Record<string, number>; // target -> rate
}

// Common well shape across the registration-success endpoints. The projection stats
// arrive either as a single object (targetCellAssignment / rawCellAssignment) or an
// array (cellAssignment / cellPaint).
interface RegistrationWell {
  WellLocation: string;
  TargetProjectionRegistrationStats?:
    | TargetProjectionRegistrationStats
    | TargetProjectionRegistrationStats[]
    | null;
}

// Coerce a well's projection stats (single, array, or missing) into a flat array.
const toTargetArray = (
  stats?: TargetProjectionRegistrationStats | TargetProjectionRegistrationStats[] | null
): TargetProjectionRegistrationStats[] =>
  stats == null ? [] : Array.isArray(stats) ? stats : [stats];

// Normalize an endpoint's well list into { wellLocation, targets }, dropping wells
// that carry no registration targets.
const normalizeWells = (wellStats?: RegistrationWell[]): NormalizedWellData[] =>
  (wellStats ?? []).flatMap((well) => {
    const targets = toTargetArray(well.TargetProjectionRegistrationStats);
    return targets.length
      ? [{ wellLocation: well.WellLocation, targets }]
      : [];
  });

interface TilePosition {
  tileName: string;
  x: number;
  y: number;
  absoluteX: number;
  absoluteY: number;
  wellLocation: string;
  tileIndex: number;
}

// Pastel red-to-green color scale for success rates
const pastelColors = [
  "#f8a5a5", // Pastel red (0%)
  "#f8bba5", // Pastel red-orange
  "#f8d4a5", // Pastel orange
  "#f8eba5", // Pastel yellow-orange
  "#f5f8a5", // Pastel yellow
  "#ddf8a5", // Pastel yellow-green
  "#c0f0a5", // Pastel light green
  "#a5e8a5", // Pastel green
  "#8fdc8f", // Pastel green (100%)
];

const getColor = (value: number, minv: number, maxv: number): string => {
  if (isNaN(value) || value === -999 || value <= -900) return "#CCCCCC";
  const normalized = (value - minv) / (maxv - minv + 0.0001);
  if (normalized > 1) return "#6fcf6f"; // Bright green for over 100%
  if (normalized < 0) return "#ff7070"; // Bright red for under 0%

  const idx = Math.min(
    Math.floor(normalized * (pastelColors.length - 1)),
    pastelColors.length - 2
  );
  const t = normalized * (pastelColors.length - 1) - idx;
  return pastelColors[Math.round(idx + t)];
};

// Fixed range for percentage values (0-100%)
const FIXED_RANGE = { min: 0, max: 100 };

const CytoRegistrationSuccess: FC<{ run: SpatialRun }> = memo(({ run }) => {
  const [selectedBatch, setSelectedBatch] = useState<string>("");
  const [selectedTarget, setSelectedTarget] = useState<string>("");
  const [tilePositions, setTilePositions] = useState<TilePosition[]>([]);
  const {
    wellTilesByLocation,
    isLoading: isWellTileOrderLoading,
  } = useRunTileLabels(run);
  const [layoutLoaded, setLayoutLoaded] = useState(false);

  // Fetch from all registration-success source endpoints
  const {
    data: cellAssignmentData,
    isLoading: isLoadingCell,
    isError: isErrorCell,
    error: errorCell,
  } = useFetch(`/spatialRun/${run?.runID}/cellAssignment`, !!run?.runID);
  console.log("cellAssignmentData", cellAssignmentData);

  const {
    data: targetCellAssignmentData,
    isLoading: isLoadingTarget,
    isError: isErrorTarget,
    error: errorTarget,
  } = useFetch(`/spatialRun/${run?.runID}/targetCellAssignment`, !!run?.runID);

  const {
    data: rawCellAssignmentData,
    isLoading: isLoadingRaw,
    isError: isErrorRaw,
    error: errorRaw,
  } = useFetch(`/spatialRun/${run?.runID}/rawCellAssignment`, !!run?.runID);

  const {
    data: cellPaintData,
    isLoading: isLoadingCellPaint,
    isError: isErrorCellPaint,
    error: errorCellPaint,
  } = useFetch(`/spatialRun/${run?.runID}/cellPaint`, !!run?.runID);

  const isLoading =
    isLoadingCell || isLoadingTarget || isLoadingRaw || isLoadingCellPaint;
  const isError =
    isErrorCell || isErrorTarget || isErrorRaw || isErrorCellPaint;
  const error = errorCell || errorTarget || errorRaw || errorCellPaint;

  // Normalize and merge data from all source endpoints
  const normalizedBatches = useMemo((): NormalizedBatch[] => {
    const batches: NormalizedBatch[] = [];
    const findBatch = (name: string) =>
      batches.find((b) => b.batchName === name);

    // A batch is worth keeping if it has per-well tile stats or any run-level rate.
    const hasContent = (
      wells: NormalizedWellData[],
      runRates: Record<string, number>
    ) => wells.length > 0 || Object.keys(runRates).length > 0;

    // cellAssignment: many targets per well, run rates keyed by target.
    Object.values((cellAssignmentData as CellAssignmentResponse) ?? {}).forEach(
      (batch) => {
        if (!batch?.BatchName) return;
        const wells = normalizeWells(batch.WellCellAssignmentStats);
        const runRates =
          batch.RunTargetProjectionRegistrationSuccessRates ?? {};
        if (hasContent(wells, runRates)) {
          batches.push({
            batchName: batch.BatchName,
            source: "cellAssignment",
            wells,
            runRates,
          });
        }
      }
    );

    // targetCellAssignment & rawCellAssignment: one target per well, one run rate.
    // Identical apart from the well-array field name; skip names already added above.
    const addSingleTargetBatches = <
      B extends {
        BatchName?: string;
        RunTargetProjectionRegistrationSuccessRate?: number;
      }
    >(
      source: NormalizedBatch["source"],
      response: Record<string, B> | undefined,
      getWells: (batch: B) => RegistrationWell[] | undefined
    ) => {
      Object.values(response ?? {}).forEach((batch) => {
        if (!batch?.BatchName || findBatch(batch.BatchName)) return;
        const wells = normalizeWells(getWells(batch));
        const target = wells[0]?.targets[0]?.Target;
        if (!wells.length || !target) return;
        batches.push({
          batchName: batch.BatchName,
          source,
          wells,
          runRates: {
            [target]: batch.RunTargetProjectionRegistrationSuccessRate ?? 0,
          },
        });
      });
    };

    addSingleTargetBatches(
      "targetCellAssignment",
      targetCellAssignmentData as TargetCellAssignmentResponse,
      (batch) => batch.WellCellTargetAssignmentStats
    );
    addSingleTargetBatches(
      "rawCellAssignment",
      rawCellAssignmentData as RawCellAssignmentResponse,
      (batch) => batch.WellCellRawAssignmentStats
    );

    // cellPaint: many targets per well (drives the tile heatmap + "Imaging Target"
    // selector), plus run rates keyed by target. Same per-well shape as cellAssignment.
    // Merge into an existing batch of the same name if present, else add a new one.
    Object.entries((cellPaintData as CellPaintResponse) ?? {}).forEach(
      ([key, batch]) => {
        const batchName = batch?.BatchName || key;
        if (!batchName) return;
        const wells = normalizeWells(batch.WellCellPaintStats);
        const runRates =
          batch.RunTargetProjectionRegistrationSuccessRates ?? {};
        if (!hasContent(wells, runRates)) return;

        const existing = findBatch(batchName);
        if (existing) {
          existing.runRates = { ...existing.runRates, ...runRates };
          if (!existing.wells.length) existing.wells = wells;
          return;
        }
        batches.push({ batchName, source: "cellPaint", wells, runRates });
      }
    );

    // Sort by batch name
    return batches.sort((a, b) => a.batchName.localeCompare(b.batchName));
  }, [
    cellAssignmentData,
    targetCellAssignmentData,
    rawCellAssignmentData,
    cellPaintData,
  ]);

  // Extract batch names from normalized data
  const batchNames = useMemo(() => {
    return normalizedBatches.map((b) => b.batchName);
  }, [normalizedBatches]);

  // Initialize selected batch when data loads
  useEffect(() => {
    if (batchNames.length > 0 && !selectedBatch) {
      setSelectedBatch(batchNames[0]);
    }
  }, [batchNames, selectedBatch]);

  // Detect well count from run
  const wellCount: WellCount = useMemo(() => {
    return tileLayoutManager.detectWellCount(run?.wellLayout || "", run?.kitType);
  }, [run?.wellLayout, run?.kitType]);

  // Load tile layout for positioning
  useEffect(() => {
    const loadLayout = async () => {
      const positions = await tileLayoutManager.loadLayout(wellCount);
      setTilePositions(positions);
      setLayoutLoaded(true);
    };
    loadLayout();
  }, [wellCount]);

  // Extract unique imaging targets from the selected batch
  const imagingTargets = useMemo(() => {
    if (!selectedBatch || normalizedBatches.length === 0) return [];

    const batch = normalizedBatches.find((b) => b.batchName === selectedBatch);
    if (!batch) return [];

    const targets = new Set<string>();
    batch.wells.forEach((well) => {
      well.targets.forEach((stats) => {
        if (stats.Target) {
          targets.add(stats.Target);
        }
      });
    });
    return Array.from(targets);
  }, [normalizedBatches, selectedBatch]);

  // Initialize/reset selected target when batch changes or data loads
  useEffect(() => {
    if (imagingTargets.length > 0) {
      // Reset target if current target is not in the new batch's targets
      if (!imagingTargets.includes(selectedTarget)) {
        setSelectedTarget(imagingTargets[0]);
      }
    }
  }, [imagingTargets, selectedTarget]);

  // Build tile values map for the selected batch and target.
  // Rate arrays are indexed by Wells[].Tiles, not the flat Tiles walk.
  const tileValues = useMemo(() => {
    const valueMap = new Map<string, number>();
    if (
      !selectedBatch ||
      !selectedTarget ||
      tilePositions.length === 0 ||
      normalizedBatches.length === 0
    ) {
      return valueMap;
    }

    const batch = normalizedBatches.find((b) => b.batchName === selectedBatch);
    if (!batch) return valueMap;

    if (isWellTileOrderLoading) return valueMap;

    if (wellTilesByLocation.size === 0) {
      console.error(
        "Missing runParameters Wells[].Tiles. Refusing to map registration tile rates without explicit tile ordering."
      );
      return valueMap;
    }

    const layoutTiles = new Set(tilePositions.map((tile) => tile.tileName));

    batch.wells.forEach((well) => {
      const targetStats = well.targets.find(
        (stats) => stats.Target === selectedTarget
      );
      if (!targetStats?.TileProjectionRegistrationSuccessRates) {
        console.error(
          `Well ${well.wellLocation}: missing TileProjectionRegistrationSuccessRates for target "${selectedTarget}".`
        );
        return;
      }

      const wellTileNames = wellTilesByLocation.get(well.wellLocation);
      if (!wellTileNames) {
        console.error(
          `Well ${well.wellLocation}: no Wells[].Tiles order found. Refusing fallback mapping.`
        );
        return;
      }

      const rates = tileRatesByName(
        wellTileNames,
        targetStats.TileProjectionRegistrationSuccessRates
      );
      rates.forEach((rate, tileName) => {
        if (!layoutTiles.has(tileName)) return;
        valueMap.set(tileName, rate * 100);
      });
    });

    return valueMap;
  }, [
    normalizedBatches,
    selectedBatch,
    selectedTarget,
    tilePositions,
    wellTilesByLocation,
    isWellTileOrderLoading,
  ]);

  // Build chart data: one bar per batch-target combination
  // Uses runRates from normalized data
  const chartData = useMemo(() => {
    if (normalizedBatches.length === 0) return [];

    const data: {
      Label: string;
      BatchName: string;
      Target: string;
      SuccessRate: number;
    }[] = [];

    normalizedBatches.forEach((batch) => {
      // Each target in the batch gets its own bar
      Object.entries(batch.runRates).forEach(([target, rate]) => {
        data.push({
          Label: `${batch.batchName} - ${target}`,
          BatchName: batch.batchName,
          Target: target,
          SuccessRate: rate * 100, // Convert to percentage
        });
      });
    });

    // Sort by batch name, then target
    return data.sort((a, b) => {
      const batchCompare = a.BatchName.localeCompare(b.BatchName);
      if (batchCompare !== 0) return batchCompare;
      return a.Target.localeCompare(b.Target);
    });
  }, [normalizedBatches]);

  if (isLoading) {
    return <div>Loading registration success data...</div>;
  }

  if (isError) {
    return (
      <div>
        Error loading data:{" "}
        {error instanceof Error ? error.message : "Unknown error"}
      </div>
    );
  }

  if (!chartData.length) {
    return <div>No registration success data available</div>;
  }

  return (
    <div>
      <h3 style={{ marginBottom: 20 }}>
        Registration Success Rate by Batch and Imaging Target
      </h3>
      <Chart dataSource={chartData}>
        <Size height={400} />
        <CommonSeriesSettings
          argumentField="Label"
          type="bar"
          barPadding={0.3}
        />
        <Series
          valueField="SuccessRate"
          name="Projection Registration Success Rate"
          color="#4CAF50"
        />
        <ValueAxis title="Registration Success Rate (%)" visualRange={[0, 100]}>
          <Grid visible={true} />
          <Label>
            <Format type="fixedPoint" precision={1} />
          </Label>
        </ValueAxis>
        <ArgumentAxis title="Batch - Imaging Target">
          <Label
            wordWrap="none"
            overlappingBehavior="rotate"
            rotationAngle={-45}
          />
        </ArgumentAxis>
        <Legend visible={false} />
        <Export enabled />
        <Tooltip enabled>
          <Format type="fixedPoint" precision={2} />
        </Tooltip>
      </Chart>

      {/* Tile-Level Registration Success Heatmap */}
      <TileRegistrationHeatmap
        batchNames={batchNames}
        selectedBatch={selectedBatch}
        setSelectedBatch={setSelectedBatch}
        imagingTargets={imagingTargets}
        selectedTarget={selectedTarget}
        setSelectedTarget={setSelectedTarget}
        tilePositions={tilePositions}
        tileValues={tileValues}
        layoutLoaded={layoutLoaded}
        isLoading={isLoading || isWellTileOrderLoading}
        wellCount={wellCount}
      />
    </div>
  );
});

// Tile Registration Heatmap Sub-component
interface TileRegistrationHeatmapProps {
  batchNames: string[];
  selectedBatch: string;
  setSelectedBatch: (batch: string) => void;
  imagingTargets: string[];
  selectedTarget: string;
  setSelectedTarget: (target: string) => void;
  tilePositions: TilePosition[];
  tileValues: Map<string, number>;
  layoutLoaded: boolean;
  isLoading: boolean;
  wellCount: WellCount;
}

const TileRegistrationHeatmap: FC<TileRegistrationHeatmapProps> = ({
  batchNames,
  selectedBatch,
  setSelectedBatch,
  imagingTargets,
  selectedTarget,
  setSelectedTarget,
  tilePositions,
  tileValues,
  layoutLoaded,
  isLoading,
  wellCount,
}) => {
  // Tooltip state
  const [hoveredTile, setHoveredTile] = useState<{
    tileName: string;
    wellLocation: string;
    value: number;
    x: number;
    y: number;
  } | null>(null);

  // Calculate display dimensions
  const SCALE = 12; // pixels per millimeter
  const TILE_WIDTH = 1.8 * SCALE;
  const TILE_HEIGHT = 2.6 * SCALE;

  // Get bounds from positions
  const bounds = useMemo(() => {
    if (tilePositions.length === 0)
      return { minX: 0, maxX: 100, minY: 0, maxY: 100 };
    const xs = tilePositions.map((t) => t.absoluteX);
    const ys = tilePositions.map((t) => t.absoluteY);
    return {
      minX: Math.min(...xs),
      maxX: Math.max(...xs),
      minY: Math.min(...ys),
      maxY: Math.max(...ys),
    };
  }, [tilePositions]);

  const containerWidth = (bounds.maxX - bounds.minX) * SCALE + TILE_WIDTH + 80;
  const containerHeight =
    (bounds.maxY - bounds.minY) * SCALE + TILE_HEIGHT + 80;

  return (
    <div style={{ marginTop: 40 }}>
      <h3 style={{ marginBottom: 20 }}>Tile-Level Registration Success Rate</h3>

      <LoadPanel
        shadingColor="rgba(0,0,0,0.4)"
        position={{ of: "#tile-registration-heatmap" }}
        visible={isLoading || !layoutLoaded}
        showIndicator={true}
        shading={true}
      />

      <div
        style={{ display: "flex", flexWrap: "wrap", gap: 20, marginBottom: 20 }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <span style={{ marginRight: 10 }}>Batch:</span>
          <SelectBox
            value={selectedBatch}
            items={batchNames}
            onValueChanged={(e) => setSelectedBatch(e.value)}
            width={120}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span style={{ marginRight: 10 }}>Imaging Target:</span>
          <SelectBox
            value={selectedTarget}
            items={imagingTargets}
            onValueChanged={(e) => setSelectedTarget(e.value)}
            width={180}
          />
        </div>
      </div>

      <div
        id="tile-registration-heatmap"
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 30,
        }}
      >
        {layoutLoaded && tilePositions.length > 0 && tileValues.size > 0 && (
          <>
            {/* Tile Heatmap */}
            <div
              style={{
                position: "relative",
                width: Math.min(containerWidth, 900),
                height: Math.min(containerHeight, 700),
                backgroundColor: "#f8f8f8",
                border: "1px solid #ddd",
                borderRadius: 8,
                overflow: "auto",
              }}
            >
              <div
                style={{
                  position: "relative",
                  width: containerWidth - 40,
                  height: containerHeight - 40,
                  margin: 20,
                }}
              >
                {tilePositions.map((tile) => {
                  const value = tileValues.get(tile.tileName) ?? NaN;
                  const pixelX = (tile.absoluteX - bounds.minX) * SCALE;
                  const pixelY = (tile.absoluteY - bounds.minY) * SCALE;

                  return (
                    <div
                      key={tile.tileName}
                      style={{
                        position: "absolute",
                        left: pixelX,
                        top: pixelY,
                        width: TILE_WIDTH,
                        height: TILE_HEIGHT,
                        backgroundColor: getColor(
                          value,
                          FIXED_RANGE.min,
                          FIXED_RANGE.max
                        ),
                        border: "1px solid #333",
                        borderRadius: 2,
                        cursor: "pointer",
                        boxSizing: "border-box",
                      }}
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setHoveredTile({
                          tileName: tile.tileName,
                          wellLocation: tile.wellLocation,
                          value,
                          x: rect.left + rect.width / 2,
                          y: rect.top,
                        });
                      }}
                      onMouseLeave={() => setHoveredTile(null)}
                    />
                  );
                })}

                {/* Fast Tooltip */}
                {hoveredTile && (
                  <div
                    style={{
                      position: "fixed",
                      left: hoveredTile.x,
                      top: hoveredTile.y - 35,
                      transform: "translateX(-50%)",
                      backgroundColor: "rgba(0, 0, 0, 0.85)",
                      color: "white",
                      padding: "6px 10px",
                      borderRadius: 4,
                      fontSize: 12,
                      whiteSpace: "nowrap",
                      pointerEvents: "none",
                      zIndex: 1000,
                    }}
                  >
                    {hoveredTile.tileName} ({hoveredTile.wellLocation}):{" "}
                    {isNaN(hoveredTile.value) || hoveredTile.value <= -900
                      ? "N/A"
                      : `${hoveredTile.value.toFixed(2)}%`}
                  </div>
                )}
              </div>
            </div>

            {/* Static Color Scale */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <div style={{ marginBottom: 5, fontSize: 12, color: "#666" }}>
                Success Rate (%)
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "stretch",
                  height: Math.min(containerHeight - 40, 400),
                }}
              >
                {/* Color gradient bar */}
                <div
                  style={{
                    width: 20,
                    height: "100%",
                    background: `linear-gradient(to bottom, ${pastelColors
                      .slice()
                      .reverse()
                      .join(", ")})`,
                    border: "1px solid #999",
                    borderRadius: 2,
                  }}
                />
                {/* Labels */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    marginLeft: 8,
                    fontSize: 11,
                    color: "#333",
                  }}
                >
                  <span>100%</span>
                  <span>75%</span>
                  <span>50%</span>
                  <span>25%</span>
                  <span>0%</span>
                </div>
              </div>
            </div>
          </>
        )}

        {layoutLoaded &&
          tilePositions.length > 0 &&
          tileValues.size === 0 &&
          !isLoading && (
            <div style={{ padding: 20, color: "#666" }}>
              No tile data available for the selected imaging target.
            </div>
          )}

        {layoutLoaded && tilePositions.length === 0 && (
          <div style={{ padding: 20, color: "#666" }}>
            Could not load tile layout for {wellCount} wells.
          </div>
        )}
      </div>

      <div style={{ marginTop: 20 }}>
        <span style={{ color: "#666", fontSize: 12 }}>
          {tilePositions.length} tiles | {wellCount} well layout | Scale: 0% -
          100%
        </span>
      </div>
    </div>
  );
};

export default CytoRegistrationSuccess;
